import styled from '@emotion/styled';
import { InnerContent, InnerTitle, TextWrapper } from '..';
import TitleWithDivider from '@/components/comm/TitleWithDivider';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';

const Container = styled.div`
display: grid;
row-gap: 16px;
`;

const CardContainer = styled.div`
display: grid;
gap: 32px;
grid-template-columns: 1fr 1fr 1fr;
grid-template-rows: 1fr 1fr;

@media (max-width: ${breakpointMedium}) {
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: ${breakpointSmall}) {
  grid-template-columns: 1fr;
}
`;

const Card = styled.div<{ slim?: boolean }>`
display: flex;
flex-direction: column;
padding: 22px 32px;
border: 1px solid ${colors.lightGrey2};
border-radius: 15px;
gap: 12px;

@media (max-width: ${breakpointMedium}) {
  height: ${({ slim }) => slim ? '126px' : '158px'};
  padding: 20px;
}
`;

const CardTitle = styled.span`
font-size: 18px;
font-weight: 700;

@media (max-width: ${breakpointMedium}) {
  font-size: 15px;
}
`;

const CardContent = styled.span`
font-size: 14px;
word-break: keep-all;

@media (max-width: ${breakpointMedium}) {
  font-size: 13px;
}
`;

export const PCCPRange = () => (
  <TitleWithDivider title="출제 범위와 합격 기준">
    <Container>
      <TextWrapper gap="5px">
        <InnerTitle>출제 범위</InnerTitle>
        <InnerContent>시험은 총 4문항으로 구성되며, 출제 범위는 아래와 같습니다.</InnerContent>
      </TextWrapper>
      <CardContainer>
        <Card>
          <CardTitle>기본 프로그램 구현</CardTitle>
          <CardContent>제시된 조건과 요구 사항을 만족하는 프로그램을 개발 하는 능력을 평가합니다.</CardContent>
        </Card>
        <Card>
          <CardTitle>초급 자료구조/알고리즘 활용</CardTitle>
          <CardContent>문자열(String), 배열(Array), 그리디(Greedy), 정렬(Sort) 등을 활용한 프로그램 개발하는 능력을 평가합니다.</CardContent>
        </Card>
        <Card>
          <CardTitle>중급 자료구조/알고리즘 활용</CardTitle>
          <CardContent>스택(Stack), 큐(Queue), 덱(Deque), 해시(Hash), 이진 탐색(Binary Search), 깊이 우선 탐색(DFS), 너비 우선 탐색(BFS) 등을 활용한 프로그램 개발하는 능력을 평가합니다.</CardContent>
        </Card>
        <Card>
          <CardTitle>고급 자료구조/알고리즘 활용</CardTitle>
          <CardContent>그래프(Graph), 트리(Tree), 힙(Heap), 다이나믹 프로그래밍(Dynamic Programming) 등을 활용한 프로그램 개발하는 능력을 평가합니다.</CardContent>
        </Card>
        <Card>
          <CardTitle>정확하고 효율적인 프로그램 작성</CardTitle>
          <CardContent>빠른 판단력으로 주어진 문제에 알맞은 자료구조와 알고리즘을 선택하여 오류없이 프로그램을 개발하는 능력을 평가합니다.</CardContent>
        </Card>
      </CardContainer>
    </Container>
  </TitleWithDivider>
);

export const PCCERange = () => (
  <TitleWithDivider title="출제 범위와 합격 기준">
    <Container>
      <TextWrapper gap="5px">
        <InnerTitle>출제 범위</InnerTitle>
        <InnerContent>시험은 총 10문항으로 구성되며, 출제 범위는 아래와 같습니다.</InnerContent>
      </TextWrapper>
      <CardContainer>
        <Card slim>
          <CardTitle>조건문</CardTitle>
          <CardContent>If, Else, If Else, 이중 If문, 조건문 속의 반복문, 반복문 속의 조건문</CardContent>
        </Card>
        <Card slim>
          <CardTitle>반복문</CardTitle>
          <CardContent>단일 반복문, 이중 반복문, 리스트, 순회, 흐름 제어</CardContent>
        </Card>
        <Card slim>
          <CardTitle>변수</CardTitle>
          <CardContent>변수 선언과 정의, 정수 타입, 실수 타입, 문자열 타입, 1차원 리스트, 2차원 리스트, 변수의 사용범위</CardContent>
        </Card>
        <Card slim>
          <CardTitle>함수</CardTitle>
          <CardContent>호출, 정의</CardContent>
        </Card>
        <Card slim>
          <CardTitle>라이브러리의 효율 및 사용</CardTitle>
          <CardContent>Math, Rand, Time</CardContent>
        </Card>
        <Card slim>
          <CardTitle>문자열</CardTitle>
          <CardContent>인덱싱, 슬라이싱, 문자열 포맷팅, 대소문자 변환, 행 변환</CardContent>
        </Card>
        <Card slim>
          <CardTitle>연산자</CardTitle>
          <CardContent>산술 연산자, 논리 연산자, 대입 연산자</CardContent>
        </Card>
        <Card slim>
          <CardTitle>출력</CardTitle>
          <CardContent>변수 출력, 문자열 출력, 형시에 맞게 출력</CardContent>
        </Card>
        <Card slim>
          <CardTitle>자료구조 및 알고리즘</CardTitle>
          <CardContent>구현, 시뮬레이션 탐색, 정렬, 다중 조건 정렬, 완전 탐색, 그리디</CardContent>
        </Card>
      </CardContainer>
    </Container>
  </TitleWithDivider>
);

export const PCSQLRange = () => (
  <TitleWithDivider title="출제 범위와 합격 기준">
    <Container>
      <TextWrapper gap="5px">
        <InnerTitle>출제 범위</InnerTitle>
        <InnerContent>시험은 총 5문항으로 구성되며, 출제 범위는 아래와 같습니다.</InnerContent>
      </TextWrapper>
      <CardContainer>
        <Card slim>
          <CardTitle>기본 SQL 문법</CardTitle>
          <CardContent>select-from절과 where절, order by절을 이용한 데이터 필터링과 추출</CardContent>
        </Card>
        <Card slim>
          <CardTitle>데이터 가공</CardTitle>
          <CardContent>날짜-시간 데이터, 문자열 데이터, group by절, 서브 쿼리(sub query), 조인(join)을 이용한 데이터 가공</CardContent>
        </Card>
        <Card slim>
          <CardTitle>중상급 쿼리 작성 및 문제 해결</CardTitle>
          <CardContent>윈도우 함수, CTE, 복잡한 조인과 집합을 이용해 다양한 문제 해결</CardContent>
        </Card>
        <Card slim>
          <CardTitle>요구사항 분석 및 상급 쿼리 작성</CardTitle>
          <CardContent>복잡한 요구사항을 분석하고, 단계적으로 접근하여 알맞은 함수와 테이블로 오류 없이 빠르게 쿼리 작성</CardContent>
        </Card>
      </CardContainer>
    </Container>
  </TitleWithDivider>
);
